import React from "react";
import { sourceLanguageNames, UiLang } from "utils/uiLanguage";

/**
 * Everything the settings panel says, in both languages.
 *
 * Kept in one file on purpose: these lines are the point of the panel — they are what turns
 * «Подготовить озвучку» and «Пересоздать озвучку» into two distinguishable actions — and a
 * translation that lives next to each control drifts out of step one control at a time.
 *
 * The source-language category is built from the lecture's own direction. A lecture translated
 * FROM English has no Russian anywhere in it, so heading its source controls «Русский оригинал»
 * would name a language that is not there; `src` carries the real one, declined for Russian.
 */
export const settingsCopy = (ui: UiLang, sourceLang: string | undefined) => {
    const en = ui === "en";
    const src = sourceLanguageNames(sourceLang, ui);

    return {
        title: en ? "Settings" : "Настройки",
        langSwitchHint: en
            ? "Language of this panel. Remembered for next time."
            : "Язык этой панели. Запомнится на следующий раз.",

        scope: (folder: string, source: string, target: string, isTranslation: boolean) =>
            en ? (
                <>
                    Everything below applies to <b>folder {folder}</b>
                    {isTranslation ? (
                        <>
                            {" "}
                            and the{" "}
                            <b>
                                {source} → {target}
                            </b>{" "}
                            pair
                        </>
                    ) : (
                        <>
                            {" "}
                            and to the <b>{source}</b> source
                        </>
                    )}
                    . Playback and view settings are yours alone — they change nothing in the files.
                </>
            ) : (
                <>
                    Действия ниже применяются к <b>папке {folder}</b>
                    {isTranslation ? (
                        <>
                            {" "}
                            и к паре{" "}
                            <b>
                                {source} → {target}
                            </b>
                        </>
                    ) : (
                        <>
                            {" "}
                            и к оригиналу <b>{source}</b>
                        </>
                    )}
                    . Настройки прослушивания и вида — только для вас, они ничего не меняют в
                    файлах.
                </>
            ),

        groups: {
            mode: en ? "Working mode" : "Режим работы",
            modeHint: en
                ? "Applies to this language pair of the lecture — for everyone who opens it, and for the final video built from it."
                : "Действует на эту языковую пару лекции — для всех, кто её откроет, и для финального видео, которое из неё соберут.",
            playback: en ? "Listening" : "Прослушивание",
            view: en ? "View" : "Вид",
            source: en ? `${src.nom} original` : `${src.nom} оригинал`,
            sourceHint: en
                ? "Editing the source text. Everything translated from it no longer matches — the translation has to be recreated."
                : "Правка исходного текста. Всё, что переведено с него, после этого расходится с ним — перевод придётся пересоздать.",
            translation: en ? "Translation" : "Перевод",
            voicing: en ? "Voicing" : "Озвучка",
            publish: en ? "Handover & publishing" : "Сдача и публикация",
            publishHint: en
                ? "From here the work moves on down the pipeline — and onto the site."
                : "Отсюда работа уходит дальше по конвейеру — и попадает на сайт.",
            service: en ? "Maintenance — diagnostics and cache" : "Служебное — диагностика и кеш",
        },

        /** Под строкой, чья кнопка в режиме «Без автоправок» выключена (см. ManualOnly/AutoEditGate). */
        autoEditOff: {
            all: en
                ? "Off in the “No automatic edits” mode — this button edits the folder by itself."
                : "Выключено режимом „Без автоправок“ — эта кнопка правит папку сама.",
            applyOnly: en
                ? "“No automatic edits” mode: the check works, applying what the machine found is off — fix it by hand."
                : "Режим „Без автоправок“: проверка работает, а применять найденное машиной выключено — правьте руками.",
            folderRuns: en
                ? "“No automatic edits” mode: folder runs with auto-fix or regenerate are off; checking the folder and voicing one selected cue work."
                : "Режим „Без автоправок“: прогон папки с автоисправлением или пересозданием выключен; проверка папки и озвучка одной выбранной реплики работают.",
        },

        rows: {
            manualOnly: {
                title: en ? "No automatic edits — manual work only" : "Без автоправок — только ручная работа",
                hint: en
                    ? "On: editing one cue saves that one cue. Overlaps are always removed: a voicing that a drag, a move or a re-voice makes run over the next one pushes that next voicing later — to its own end + 50 ms, and on down the line only while they still overlap; the push is named on screen. Nothing is ever moved earlier, gaps are not closed, speed is not fitted. Re-voicing keeps the cue's place; merging source cues does not re-translate. Buttons that edit the folder by themselves are off: «Prepare the voicing», «Rebuild the voicing», resync, translating the whole folder, recreating from the source and «into every language», fixing the source, «apply» in diagnostics and post-processing, folder runs with auto-fix; checks that only show still work. The final video is built from the files as you left them — the only thing moved is a voicing that would still overlap: the build places its sound right at the previous one's end, later, never earlier; nothing is re-voiced or re-translated for you."
                    : "Включено: правка одной реплики сохраняет только её. Наложения снимаются всегда: если после перетаскивания, сдвига или переозвучки озвучка наехала на следующую, следующая сдвигается позже — на её конец + 50 мс, и дальше по цепочке, только пока они наезжают; такой сдвиг называется на экране. Раньше ничего не сдвигается, зазоры не закрываются, скорость не подгоняется. Переозвучка не трогает место реплики; склейка реплик оригинала не переводит заново. Кнопки, которые правят папку сами, выключены: «Подготовить озвучку», «Пересоздать озвучку», ресинк, перевод всей папки, пересоздание по оригиналу и «во все языки», исправление оригинала, «применить» в диагностике и постобработке, прогон папки с автоисправлением; проверки, которые только показывают, работают. Финальное видео собирается из файлов такими, какими вы их оставили, — сдвигается только озвучка, которая всё ещё наезжает: сборка ставит её звук ровно на конец предыдущей, позже, никогда не раньше; ничего не переозвучивается и не переводится за вас.",
            },
            speed: {
                title: en ? "Playback speed" : "Скорость воспроизведения",
                hint: en
                    ? "Listening only — the files themselves are untouched."
                    : "Только для прослушивания — на сами файлы не влияет.",
            },
            balance: {
                title: en ? "Volume balance" : "Баланс громкости",
                hint: en
                    ? "Left is translation only, right is original only. Always adds up to 100%."
                    : "Слева — только перевод, справа — только оригинал. Сумма всегда 100%.",
            },
            muteOriginal: {
                title: en
                    ? "Mute the original when a voicing is clicked"
                    : "Глушить оригинал при клике по озвучке",
                hint: en
                    ? "Off — both tracks are heard together. Ctrl/Cmd while clicking does the opposite once."
                    : "Выключено — слышно обе дорожки сразу. Ctrl/Cmd при клике даёт разово наоборот.",
            },
            zoom: {
                title: en ? "Track zoom" : "Масштаб дорожек",
                hint: en
                    ? "How many seconds fit on the screen."
                    : "Сколько секунд помещается на экране.",
            },
            fontSource: (code: string) =>
                en ? `Subtitle size — original (${code})` : `Размер субтитров — оригинал (${code})`,
            fontTarget: (code: string) =>
                en ? `Subtitle size — translation (${code})` : `Размер субтитров — перевод (${code})`,
            globalTime: {
                title: en ? "Time from the start of the video" : "Время от начала видео",
                hint: en
                    ? "A lecture is cut into folders of a few minutes, and a cue's time is counted from the start of its folder. This shows the time from the start of the whole video underneath it; the folder time stays."
                    : "Лекция нарезана на папки по несколько минут, и время реплики считается от начала папки. Показывать под ним время от начала всего видео — время папки остаётся на месте.",
            },
            followPlayback: {
                title: en ? "Follow the playback" : "Вести ленту за воспроизведением",
                hint: en
                    ? "The list scrolls itself so the cue being heard stays on screen, a third from the top. Scroll by hand and following steps aside for a few seconds, then resumes."
                    : "Лента прокручивается сама, чтобы звучащая реплика оставалась на экране — на трети сверху, чтобы было видно, что дальше. Прокрутите рукой — слежение отступит на несколько секунд и вернётся само.",
            },
            freeMovement: {
                title: en ? "Free movement" : "Свободное перемещение",
                hint: en
                    ? "Lifts the limits on dragging voicings and subtitles. Easy to knock out of sync — for debugging only."
                    : "Снимает ограничения на перетаскивание озвучки и субтитров. Легко разъехаться — только для отладки.",
            },
            fixSource: {
                title: en ? `Fix ${src.adjPl} subtitles` : `Исправить ${src.adjPl} субтитры`,
                hint: en ? (
                    <>
                        Full cycle over the folder: STT → rebuild → gaps → merge → punctuation. Next
                        to it, «Restore state» rolls back to the saved snapshot: the folder&apos;s
                        current {src.adjPl} subtitles and their audio are deleted.
                    </>
                ) : (
                    <>
                        Полный цикл по папке: STT → перестройка → пропуски → объединение →
                        пунктуация. Рядом «Восстановить состояние» — откат к сохранённому снимку:
                        нынешние {src.adjPl} субтитры папки и их озвучка удаляются.
                    </>
                ),
            },
            voids: {
                title: en ? "Removing pauses in the original" : "Удаление пауз в оригинале",
                hint: en
                    ? "Cut long pauses out of the source track and download the shortened video."
                    : "Вырезать длинные паузы из исходной дорожки и скачать укороченное видео.",
            },
            markSourceDone: {
                title: en
                    ? `Mark the ${src.acc} as finished`
                    : `Пометить ${src.acc} готовым`,
                hint: en
                    ? `Just a flag for translators: the ${src.acc} in this folder will not change again. It starts nothing.`
                    : `Просто флаг для переводчиков: ${src.acc} в этой папке больше не меняется. Ничего не запускает.`,
            },
            translateAll: {
                title: en ? "Translate the whole folder" : "Перевести всю папку",
                hint: en
                    ? "Translates the folder's cues again; the dialog can allow overwriting ones already done."
                    : "Переводит реплики папки заново; в диалоге можно разрешить перезапись уже готовых.",
            },
            recreateFromSource: {
                title: en
                    ? `Recreate the translation from ${src.acc} edits`
                    : "Пересоздать перевод по правкам оригинала",
                hint: en
                    ? "Only the cues whose source text changed, in the current language pair."
                    : "Только те реплики, где менялся оригинал, — в текущей языковой паре.",
            },
            allLanguages: {
                title: en ? "The same, into every language" : "То же во все языки",
                hint: en
                    ? "Recreates this folder's translation and voicing in every language pair. Assembly and publishing stay manual."
                    : "Пересоздаёт перевод и озвучку этой папки во всех языковых парах. Склейка и публикация — потом и вручную.",
            },
            postProcess: {
                title: en ? "Translation post-processing" : "Постобработка перевода",
                hint: en
                    ? "One aspect at a time: speech cleanup, pronouns, terms, style. Edits are proposed as a list — you apply them."
                    : "Проверка одного аспекта за раз: очистка речи, местоимения, термины, стиль. Правки предлагаются списком — применяете вы.",
            },
            prepareVoicing: {
                title: en ? "Prepare the voicing" : "Подготовить озвучку",
                hint: en
                    ? "Checks and repairs the translation before synthesis: missing cues, missing audio, wrong voices, timings."
                    : "Проверяет и чинит перевод перед синтезом: пропущенные реплики, отсутствующее аудио, чужие голоса, тайминги.",
            },
            checkedSynthesis: {
                title: en ? "Voicing with a check" : "Создание озвучки с проверкой",
                hint: en
                    ? "Synthesis with an automatic check of the result. The switch beside it makes the note icon work in auto-fix mode."
                    : "Синтез с автоматической проверкой результата. Переключатель рядом заставляет иконку заметки работать в режиме авточинки.",
            },
            resync: {
                title: en ? "Resync translations" : "Ресинк переводов",
                hint: en
                    ? "Aligns voicing timings to the source timeline — selected cues or all of them. Can be undone."
                    : "Выравнивает тайминги озвучки по исходной шкале — выбранные реплики или все. Есть откат.",
            },
            rebuildVoicing: {
                title: en ? "Rebuild the voicing" : "Пересоздать озвучку",
                hint: en
                    ? "Re-synthesises EVERY voiceover in the folder and resets positions. Manual placement is lost."
                    : "Пересинтезирует ВСЮ озвучку папки заново и сбрасывает позиции. Ручные правки положения пропадут.",
            },
            handIn: {
                title: en ? "Folder ready — hand it on" : "Папка готова — в конвейер",
                hint: en
                    ? "Marks the folder handed in and starts the rest: voicing → final video → publishing."
                    : "Помечает папку сданной и запускает остальное: озвучка → финальное видео → публикация.",
            },
            updateHls: {
                title: en ? "Update the stream's HLS" : "Обновить HLS стрима",
                hint: en
                    ? "Rebuilds and uploads the current folder's HLS. Only for lectures recorded as a stream."
                    : "Пересобирает и выкладывает HLS текущей папки. Только для лекций, записанных стримом.",
            },
            reprocessClip: {
                title: en ? "Rebuild and publish the clip" : "Пересобрать и опубликовать клип",
                hint: en
                    ? "Replaces the clip's subtitles on R2 with your edit. Only when the editor is open for a clip."
                    : "Заменяет субтитры клипа на R2 вашей правкой. Только когда редактор открыт для клипа.",
            },
            republishState: {
                title: en ? "Republish state" : "Состояние переопубликации",
                hint: en
                    ? "What is happening to this part of the lecture on the publishing side."
                    : "Что сейчас происходит с этой частью лекции на стороне публикации.",
            },
            managementBoard: {
                title: en ? "Management board" : "Доска управления",
                hint: en
                    ? "Overall status of lectures and folders — opens in another tab."
                    : "Общий статус лекций и папок — откроется в соседней вкладке.",
            },
            diagnose: {
                title: en ? "Folder diagnostics" : "Диагностика папки",
                hint: en
                    ? "Looks for problems — overlaps, gaps, voice mismatches — and lets you fix them one by one."
                    : "Ищет проблемы — наложения, пропуски, несовпадение голосов — и даёт исправить их по одной.",
            },
            checkData: {
                title: en ? "Data completeness check" : "Проверка полноты данных",
                hint: en
                    ? "A technical summary across the lecture's folders: what is missing where."
                    : "Техническая сводка по всем папкам лекции: где чего не хватает.",
            },
            clearCache: {
                title: en ? "Clear the local cache" : "Очистить локальный кеш",
                hint: en
                    ? "Deletes audio and video downloaded into this browser. Nothing changes on the server — it helps when the editor shows something stale."
                    : "Удаляет скачанные аудио и видео из браузера. На сервере ничего не меняется — помогает, если редактор показывает старое.",
            },
        },
    };
};

export type SettingsCopy = ReturnType<typeof settingsCopy>;
